import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { roleKeys, roleLevelKeys } from "@/lib/query";

/**
 * One catalogue level as it relates to one role. (Backend twin:
 * api/role-level-applicability.ts.)
 */
export interface RoleLevelOption {
  id: string;
  name: string;
  definition: string | null;
  sortOrder: number;
  retired: boolean;
  /** Whether this role currently opts into the level. */
  applies: boolean;
  /** People holding this role at this level — blocks removal when > 0. */
  peopleCount: number;
  /** The signed-in viewer holds this role at this level — WI-105's "Your level". */
  heldByViewer: boolean;
}

/**
 * Every catalogue level annotated for one role. Read by the Manage-levels
 * dialog and by the role's Levels section; safe for any signed-in user, since
 * only writing is admin-gated.
 */
export function useRoleLevelOptions(roleId: string, enabled = true) {
  return useQuery({
    queryKey: roleLevelKeys.forRole(roleId),
    enabled: enabled && roleId !== "",
    queryFn: async (): Promise<RoleLevelOption[]> => {
      const res = await fetch(`/api/roles/${roleId}/levels`);
      if (!res.ok) throw new Error("Failed to fetch the role's levels");
      const json = await res.json();
      return json.data;
    },
  });
}

/**
 * Replace which levels a role uses. The server refuses to remove a level that
 * still has people at it and returns a message naming each blocker — surface it
 * verbatim rather than paraphrasing.
 *
 * The role id rides in the mutation variables rather than being bound at hook
 * construction, because the create flow doesn't know the id until the role
 * exists. No success toast: this runs as part of the role save, so the role
 * mutation owns the confirmation and two toasts would double up.
 */
export function useSetRoleLevels() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ roleId, roleLevelIds }: { roleId: string; roleLevelIds: string[] }) => {
      const res = await fetch(`/api/roles/${roleId}/levels`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ roleLevelIds }),
      });
      if (!res.ok) {
        const body: unknown = await res.json().catch(() => null);
        const message =
          body !== null &&
          typeof body === "object" &&
          "error" in body &&
          typeof body.error === "string"
            ? body.error
            : "Failed to update the role's levels";
        throw new Error(message);
      }
      return res.json();
    },
    onSuccess: (_data, { roleId }) => {
      qc.invalidateQueries({ queryKey: roleLevelKeys.all });
      // The catalogue's "roles using it" count and the role detail both move.
      qc.invalidateQueries({ queryKey: roleKeys.detail(roleId) });
    },
    // The people-guard refusal ("Can't remove Staff (4 people) — move those
    // people first") is the whole reason this can fail, so it MUST reach the
    // user. Surfaced verbatim from the server rather than paraphrased.
    onError: (e: Error) => toast.error(e.message || "Failed to update the role's levels"),
  });
}
